/**
 * Profile view — F13B (sovereign book titled to client).
 * Subdomain, offers in market, business identity.
 */

import { useState, useEffect } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";

export function ProfileView({ isOperator }: { isOperator: boolean }) {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ subdomain: "", display_name: "", bio: "" });
  const [offersCount, setOffersCount] = useState(0);

  useEffect(() => {
    const clients = getClients();
    if (!clients) { setLoading(false); return; }
    clients.control.getProfile()
      .then((res: any) => {
        setProfile(applyMembrane(res, isOperator));
        setForm({
          subdomain: res?.subdomain || "",
          display_name: res?.display_name || "",
          bio: res?.bio || "",
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    // Fetch offers count
    clients.control.listOffers()
      .then((res: any) => { setOffersCount((res?.offers || res || []).length); })
      .catch(() => {});
  }, [isOperator]);

  async function save() {
    const clients = getClients();
    if (!clients) return;
    try {
      await clients.control.updateProfile(form);
      setEditing(false);
      const res: any = await clients.control.getProfile();
      setProfile(applyMembrane(res, isOperator));
    } catch (err: any) { setError(err.message); }
  }

  if (loading) return <div className="loading">Loading profile…</div>;

  return (
    <div className="view">
      <div className="view-header">
        <span className="eyebrow">{applyMembrane("F13B", isOperator)}</span>
        <h1>Profile</h1>
        <button className="btn-secondary" onClick={() => setEditing(!editing)}>
          {editing ? "Cancel" : "Edit"}
        </button>
      </div>
      {error && <div className="error-state">{error}</div>}
      {editing ? (
        <div className="card form-card">
          <div className="form-row">
            <label>Subdomain</label>
            <input className="text-input" value={form.subdomain} onChange={(e) => setForm({...form, subdomain: e.target.value})} placeholder="yourbusiness" />
            <span className="muted">.getrida.work</span>
          </div>
          <div className="form-row">
            <label>Display Name</label>
            <input className="text-input" value={form.display_name} onChange={(e) => setForm({...form, display_name: e.target.value})} placeholder="Your Business Name" />
          </div>
          <div className="form-row">
            <label>Bio</label>
            <textarea className="text-input" value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})} rows={3} placeholder="Tell visitors about your business" />
          </div>
          <button className="btn-primary" onClick={save}>Save</button>
        </div>
      ) : (
        <div className="card">
          <div className="card-title">{profile?.display_name || "Your Business"}</div>
          <div className="object-fields">
            <div className="object-field">
              <span className="object-field-label">Subdomain</span>
              <span className="object-field-value">{profile?.subdomain ? `${profile.subdomain}.getrida.work` : "Not set"}</span>
            </div>
            <div className="object-field">
              <span className="object-field-label">Bio</span>
              <span className="object-field-value">{profile?.bio || "—"}</span>
            </div>
            <div className="object-field">
              <span className="object-field-label">Offers in market</span>
              <span className="object-field-value">{offersCount}</span>
            </div>
            <div className="object-field">
              <span className="object-field-label">Published</span>
              <span className="object-field-value">
                <span className="badge" data-status={profile?.published ? "active" : "pending"}>
                  {profile?.published ? "Published" : "Draft"}
                </span>
              </span>
            </div>
          </div>

          {/* Preview link to subdomain */}
          {profile?.subdomain && (
            <div style={{ marginTop: 12 }}>
              <a
                href={`https://${profile.subdomain}.getrida.work`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                style={{ display: "inline-block", textDecoration: "none" }}
              >
                Preview {profile.subdomain}.getrida.work →
              </a>
            </div>
          )}

          {/* Publish button */}
          <div style={{ marginTop: 10 }}>
            <button
              className="btn-primary"
              onClick={async () => {
                const clients = getClients();
                if (!clients) return;
                try {
                  await clients.control.updateProfile({ ...form, published: !profile?.published });
                  const res: any = await clients.control.getProfile();
                  setProfile(applyMembrane(res, isOperator));
                } catch (err: any) { setError(err.message); }
              }}
            >
              {profile?.published ? "Unpublish" : "Publish"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
